import IconLoader from "@/features/Common/components/IconLoader";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  WellnessCategoryCardProps,
  WellnessScoreStatus,
} from "../Common/types";
import { metrics } from "./data";
import HealthScoreIndicator from "../Common/HealthScoreIndicator";
import CollapsableMetricsList from "../Common/CollapsableMetricsList";
import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import WellnessCategoryCard from "../WellnessCategoryCard";
import LifestyleFlyupModal from "./LifestyleFlyupModal";
import { useLifestyleHealthSDK } from "./Hooks";
import LifestyleSuccessScreen from "./LifestyleSuccessScreen";
import LifestyleDashboard from "./LifestyleDashboard";
import { useMachine } from "@xstate/react";
import {
  lifestyleFlowMachine,
  eventNames,
  LifestyleFlowState,
} from "./machine";
import { useNavigate } from "react-router";

export default function LifestyleCard() {
  const { t } = useTranslation();
  const [score, setScore] = useState<number | null>(null);
  const navigate = useNavigate();

  const [flowState, send] = useMachine(lifestyleFlowMachine);

  const {
    handleConnect,
    handleFetchData,
    isConnected,
    hasAnyPermissions,
    isAvailable,
    permissions,
    records,
  } = useLifestyleHealthSDK();

  useEffect(() => {
    setScore(78);
  }, []);

  // Watch for permission changes when awaiting - use ref to prevent multiple triggers
  const hasTriggeredPermissionsRef = React.useRef(false);

  useEffect(() => {
    // Reset the ref when we go back to awaiting state
    if (flowState.matches(LifestyleFlowState.AwaitingPermissions)) {
      hasTriggeredPermissionsRef.current = false;
    }
  }, [flowState]);

  useEffect(() => {
    if (
      flowState.matches(LifestyleFlowState.AwaitingPermissions) &&
      (isConnected || hasAnyPermissions) &&
      !hasTriggeredPermissionsRef.current
    ) {
      hasTriggeredPermissionsRef.current = true;
      send({ type: eventNames.PERMISSIONS_GRANTED });
    }
  }, [flowState, isConnected, hasAnyPermissions, send]);

  const lifestyleProps: WellnessCategoryCardProps = {
    icon: (
      <IconLoader icon="LifestyleIcon" color="primary" sx={{ fontSize: 40 }} />
    ),
    title: "Lifestyle",
    status: WellnessScoreStatus.Healthy,
    description: t(
      score ? "Lifestyle.description.update" : "Lifestyle.description"
    ),
    lastUpdated: "2026-01-15T11:25:27.3778572+00:00",
  };

  return (
    <React.Fragment>
      <WellnessCategoryCard {...lifestyleProps}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            mt: 3,
          }}
        >
          {score && <HealthScoreIndicator healthScore={score} />}

          {score && <CollapsableMetricsList metrics={metrics} />}
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/home/health-score/lifestyle-questionnaire")}
          fullWidth
        >
          {t("Lifestyle.buttonLabel.primary")}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            if (isConnected || hasAnyPermissions) {
              send({ type: eventNames.OPEN_SUCCESS });
            } else {
              send({ type: eventNames.OPEN_FLYUP });
            }
          }}
          fullWidth
          disabled={!isAvailable}
        >
          {isConnected || hasAnyPermissions
            ? t("Lifestyle.buttonLabel.secondary.update")
            : t("Lifestyle.buttonLabel.secondary")}
        </Button>
      </WellnessCategoryCard>

      <LifestyleFlyupModal
        isOpen={flowState.matches(LifestyleFlowState.FlyupModal)}
        onClose={() => send({ type: eventNames.CLOSE_FLYUP })}
        onConnect={() => {
          handleConnect();
          send({ type: eventNames.CONNECT_CLICKED });
        }}
      />

      <LifestyleSuccessScreen
        isOpen={
          flowState.matches(LifestyleFlowState.Success) &&
          (isConnected || hasAnyPermissions)
        }
        onClose={() => send({ type: eventNames.CLOSE_SUCCESS })}
        onContinue={() => send({ type: eventNames.CONTINUE_TO_DASHBOARD })}
      />

      <LifestyleDashboard
        isOpen={flowState.matches(LifestyleFlowState.Dashboard)}
        onBack={() => send({ type: eventNames.BACK_TO_IDLE })}
        isConnected={isConnected}
        hasAnyPermissions={hasAnyPermissions}
        isAvailable={isAvailable}
        permissions={permissions}
        records={records}
        onFetchData={handleFetchData}
      />
    </React.Fragment>
  );
}
